import React, { useState } from 'react';
import { mockPendingInvites } from '../../data/mockData';
import type { PendingInvite } from '../../types';
import { MoreIcon, CloseIcon, ChevronDownIcon, DeleteMemberIcon } from '../icons';

// Modal component
const Modal: React.FC<{ isOpen: boolean; onClose: () => void; children: React.ReactNode }> = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" onClick={onClose} role="dialog" aria-modal="true">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-lg m-4" onClick={(e) => e.stopPropagation()}>
                {children}
            </div>
        </div>
    );
};


// Invite Member Modal Content
const InviteMemberModal: React.FC<{ onClose: () => void; onInvite: (invite: Omit<PendingInvite, 'id' | 'inviteDate'>) => void }> = ({ onClose, onInvite }) => {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('');

    const isFormValid = fullName.trim() !== '' && email.trim() !== '' && role !== '';

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid) return;

        const initials = fullName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
        
        onInvite({
            name: fullName,
            email,
            initials,
            bgColor: 'bg-orange-200 text-orange-800' // a default color
        });
        onClose();
    };
    
    return (
        <div className="p-8">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Invite a Member</h2>
                    <p className="text-sm text-gray-500 mt-1">Invite members to manage task.</p>
                </div>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close modal">
                    <CloseIcon className="w-6 h-6" />
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="full-name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input type="text" id="full-name" placeholder="Enter Name" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"/>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <input type="email" id="email" placeholder="eg, shola@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"/>
                    </div>
                    <div>
                        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <div className="relative">
                            <select id="role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full appearance-none bg-white px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent">
                                <option value="" disabled>Select option</option>
                                <option value="Administrator">Administrator</option>
                                <option value="Editor">Editor</option>
                                <option value="Viewer">Viewer</option>
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                            </div>
                        </div>
                    </div>
                </div>
                <button 
                    type="submit" 
                    disabled={!isFormValid} 
                    className={`w-full font-semibold py-3 px-4 rounded-lg transition-colors text-sm text-white ${isFormValid ? 'bg-[#047857] hover:bg-[#059669]' : 'bg-gray-400 cursor-not-allowed'}`}
                >
                    Send Invite
                </button>
            </form>
        </div>
    );
};


// Delete Member Modal Content
const DeleteMemberModal: React.FC<{ onClose: () => void; onConfirm: () => void; }> = ({ onClose, onConfirm }) => {
    return (
        <div className="p-8 text-center">
            <div className="mx-auto mb-4">
                <DeleteMemberIcon />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Delete Member?</h2>
            <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">Are you sure you want to delete this member? They will lose all access.</p>
            <div className="flex justify-center gap-4 mt-8">
                <button onClick={onConfirm} className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 px-10 rounded-lg transition-colors">
                    Delete
                </button>
                <button onClick={onClose} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2.5 px-10 rounded-lg transition-colors">
                    Cancel
                </button>
            </div>
        </div>
    );
};


const AdminUsers: React.FC = () => {
    const [isInviteModalOpen, setInviteModalOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [inviteToDelete, setInviteToDelete] = useState<PendingInvite | null>(null);
    const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>(mockPendingInvites);

    const handleOpenDeleteModal = (invite: PendingInvite) => {
        setInviteToDelete(invite);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (inviteToDelete) {
            setPendingInvites(invites => invites.filter(i => i.id !== inviteToDelete.id));
        }
        setDeleteModalOpen(false);
        setInviteToDelete(null);
    };

    const handleAddInvite = (newInviteData: Omit<PendingInvite, 'id' | 'inviteDate'>) => {
        const newInvite: PendingInvite = {
            ...newInviteData,
            id: Date.now(),
            inviteDate: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        };
        setPendingInvites(prev => [newInvite, ...prev]);
    };

    return (
        <>
            <div className="space-y-8">
                <div className=" bg-gray-200 text-black rounded-xl shadow-sm p-6">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h2 className="text-xl font-bold">Members</h2>
                            <p className="text-sm text-gray-800 mt-1">Invite your team members to work faster and collaborate together. Manage their permissions.</p>
                        </div>
                        <button 
                            onClick={() => setInviteModalOpen(true)}
                            className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm whitespace-nowrap">
                            Invite Member
                        </button>
                    </div>
                    <div className="text-center py-12">
                        <div className="inline-block bg-gray-800 p-4 rounded-full">
                            <svg className="w-12 h-12 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m-7.5-2.962a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18 18.72v-2.172c0-.962.784-1.75 1.75-1.75h.5c.966 0 1.75.788 1.75 1.75v2.172M12.121 12.121a3 3 0 10-4.242 0 3 3 0 004.242 0zM19.5 12c0 .245-.02.486-.058.722m-2.146-4.322a3 3 0 014.243 4.242" />
                            </svg>
                        </div>
                        <h3 className="mt-4 text-md font-semibold text-gray-300">No Members Yet</h3>
                        <p className="mt-1 text-sm text-gray-400">You are yet to invite a member</p>
                        <button
                             onClick={() => setInviteModalOpen(true)}
                             className="mt-6 bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-5 rounded-lg transition-colors text-sm">
                            Invite Member
                        </button>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-800">Pending Invite</h2>
                        <p className="text-sm text-gray-500 mt-1">Invite your team members to work faster and collaborate together. Manage their permissions.</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-white">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invite Date</th>
                                    <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {pendingInvites.map((invite: PendingInvite) => (
                                    <tr key={invite.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold mr-4 ${invite.bgColor}`}>
                                                    {invite.initials}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-gray-900">{invite.name}</div>
                                                    <div className="text-sm text-gray-500">{invite.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invite.inviteDate}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <button 
                                                onClick={() => handleOpenDeleteModal(invite)}
                                                className="text-gray-500 hover:text-gray-800 p-1 rounded-full hover:bg-gray-200/20"
                                                aria-label={`Actions for ${invite.name}`}
                                            >
                                                <MoreIcon className="w-5 h-5"/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <Modal isOpen={isInviteModalOpen} onClose={() => setInviteModalOpen(false)}>
                <InviteMemberModal onClose={() => setInviteModalOpen(false)} onInvite={handleAddInvite} />
            </Modal>
            
            <Modal isOpen={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
                <DeleteMemberModal onClose={() => setDeleteModalOpen(false)} onConfirm={handleConfirmDelete} />
            </Modal>
        </>
    );
};

export default AdminUsers;